import { Checkbox, Grid, MenuItem, Paper, TextField } from '@mui/material'
import { styled } from '@mui/material/styles'
import { Country } from 'country-state-city'
import { ICountry } from 'country-state-city/lib/interface'
import {
    FormikContextType,
    FormikErrors,
    FormikTouched,
    useFormikContext,
} from 'formik'
import { ChangeEvent, useContext, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import {
    ASIAN_LANGUAGES,
    SYNDEO_LANGUAGES,
    UserRole,
} from '../../../utils/constants'
import {
    conditionalClasses,
    formatPhoneNumber,
    GENERATE_POLICY_LINK,
} from '../../../utils/helper-functions'
import { OnboardingStepsFormType } from '../../../utils/types/auth'
import AuthCountrySelection from '../../auth-country-selection'

import { AuthContext } from '../../../providers/contexts/Auth'
import { OnboardingFormContext } from '../../../providers/contexts/OnboardingForm'
import { ROUTES } from '../../../utils/constants/enum'
import { useLocalState } from '../../../utils/local-storage'
import { emailRegex } from '../../../utils/regex'
import PhoneCodeSelection from '../../dashboard/phone-code-selection'
import './onboarding-step1.scss'

const Countries = Country.getAllCountries() as ICountry[]

const Item = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(1),
    marginBottom: '27px',
}))

const OnboardingStepOne = () => {
    const {
        values,
        touched,
        errors,
        handleChange,
        handleBlur,
        setFieldValue,
        setFieldError,
    }: FormikContextType<OnboardingStepsFormType> = useFormikContext()

    const { t, i18n } = useTranslation()

    const [language, setLanguage] = useLocalState<string>('i18nextLng', true)
    const { setOnboardingDetails, otpSession } = useContext(
        OnboardingFormContext
    )

    const { userName } = useContext(AuthContext)

    const { mlSignupError } = useContext(OnboardingFormContext)

    useEffect(() => {
        if (mlSignupError) {
            const errorMessage =
                typeof mlSignupError === 'string'
                    ? mlSignupError
                    : mlSignupError === true
                    ? t('common_elements.something_wrong_text')
                    : ''
            setFieldError('mobilePhoneNumber', errorMessage)
        }
    }, [mlSignupError, errors, t, setFieldError])

    useEffect(() => {
        const data = {
            session: otpSession,
            acceptTerms: values.acceptTerms,
            appUserKey: values.appUserKey,
            businessName: values.businessName ?? '',
            country: values.countrySelected.toLowerCase(),
            emailAddress: values.emailAddress.trim(),
            firstName: values.firstName,
            lastName: values.lastName,
            mobilePhoneNumber: values.mobilePhoneNumber
                .trim()
                .replace(/[-.\s()]/g, ''),
            code: values.otp ?? '',
            optInMarketingByEmail: values.emailConfirmation,
            optInMarketingByPhone: values.emailConfirmation,
            userRole: values.userRole,
            beautyHealthCustomerId: values.beautyHealthCustomerId,
            optInDataSharingAgreement: values.optInDataSharingAgreement,
        }
        setOnboardingDetails(data)
    }, [values, otpSession, setOnboardingDetails])
    const handleSelectLanguage = (e: ChangeEvent<HTMLInputElement>) => {
        setLanguage(e.target.value)
        i18n.changeLanguage(e.target.value)
    }

    const countrySelected = useMemo(
        () =>
            Countries.find(
                (country) =>
                    country.isoCode.toLowerCase() ===
                    values.phoneCountry?.toLowerCase()
            ),
        [values.phoneCountry]
    )

    const selectedCountry = useMemo(
        () =>
            Countries.find(
                (country) =>
                    country.isoCode.toLowerCase() ===
                    values.countrySelected.toLowerCase()
            ),
        [values.countrySelected]
    )

    const getTranslationKey = (fieldName: string): string => {
        const fieldMapping: { [key: string]: string } = {
            firstName: 'onboarding_name_page.onboarding_name_first_name',
            lastName: 'onboarding_name_page.onboarding_name_last_name',
            acceptTerms: 'accept_terms',
            optInDataSharingAgreement: 'opt_in_data_sharing_agreement',
            emailConfirmation:
                'onboarding_email_page.onboarding_email_contact_opt_in',
        }

        return `${fieldMapping[fieldName]}`
    }
    const isUSandCA = (country: string) => {
        const countryList = ['us', 'ca']
        return countryList.includes(country)
    }

    useEffect(() => {
        if (userName.length) {
            const isEmail = emailRegex.test(userName)
            const fieldName = isEmail ? 'emailAddress' : 'mobilePhoneNumber'
            const trimmedValue = userName.trim()

            const updatedReadOnlyField = new Set(values.readOnlyField || [])
            updatedReadOnlyField.add(fieldName)
            setFieldValue(fieldName, trimmedValue)
            setFieldValue('readOnlyField', Array.from(updatedReadOnlyField))
        }
    }, [userName])

    return (
        <div className="onboarding-step1 signup-forms">
            <Grid className="language-wrapper">
                <Item id="language">
                    <TextField
                        select
                        className="mais large-width"
                        name="language"
                        placeholder="Language"
                        value={language ?? 'en'}
                        onChange={handleSelectLanguage}
                    >
                        <MenuItem value="language" disabled>
                            {t('sign_in_page.language_placeholder')}
                        </MenuItem>
                        {SYNDEO_LANGUAGES.map(({ code, name }) => (
                            <MenuItem key={code} value={code}>
                                {name}
                            </MenuItem>
                        ))}
                    </TextField>
                </Item>
            </Grid>

            <Grid className="location-dropdown">
                <p>{t('onboarding_name_page.located_in_text')}</p>
                <AuthCountrySelection
                    values={values}
                    countrySelected={selectedCountry}
                    handleChange={(event) => {
                        const { value } = event.target
                        setFieldValue('countrySelected', value)
                        if (!values.mobilePhoneNumber) {
                            setFieldValue('phoneCountry', value)
                        }
                    }}
                />
            </Grid>

            <div
                className={`${
                    language && ASIAN_LANGUAGES.includes(language)
                        ? 'field-reverse'
                        : ''
                }`}
            >
                {['firstName', 'lastName'].map((field) => (
                    <Grid
                        key={field}
                        className="inp-space input-field"
                        item
                        xs={12}
                    >
                        <TextField
                            className={`mais ${conditionalClasses(
                                !!touched[
                                    field as keyof FormikTouched<OnboardingStepsFormType>
                                ] &&
                                    !!errors[
                                        field as keyof FormikErrors<OnboardingStepsFormType>
                                    ],
                                'errorBorder'
                            )}`}
                            name={field}
                            variant="standard"
                            placeholder={t(`${getTranslationKey(field)}`) ?? ''}
                            value={
                                values[field as keyof OnboardingStepsFormType]
                            }
                            error={
                                !!touched[
                                    field as keyof FormikTouched<OnboardingStepsFormType>
                                ] &&
                                !!errors[
                                    field as keyof FormikErrors<OnboardingStepsFormType>
                                ]
                            }
                            onChange={handleChange}
                            onBlur={(e) => {
                                handleBlur(e)
                                setFieldValue(
                                    field,
                                    e.target.value
                                        .replace(/^[\s\-_'.]+/, '')
                                        .replace(/[\s\-_']+$/, '')
                                )
                            }}
                        />
                        {touched[
                            field as keyof FormikTouched<OnboardingStepsFormType>
                        ] &&
                            errors[
                                field as keyof FormikErrors<OnboardingStepsFormType>
                            ] && (
                                <div className="error-txt">
                                    {t(
                                        errors[
                                            field as keyof FormikErrors<OnboardingStepsFormType>
                                        ] ?? ''
                                    )}
                                </div>
                            )}
                    </Grid>
                ))}
            </div>

            <div className="onboarding-step2 signup-forms">
                <Grid
                    className="inp-space"
                    item
                    xs={12}
                    onBlur={(e) => {
                        handleBlur(e)
                        if (values.mobilePhoneNumber) {
                            setFieldValue(
                                'mobilePhoneNumber',
                                formatPhoneNumber(values.mobilePhoneNumber)
                            )
                        }
                    }}
                >
                    <PhoneCodeSelection
                        handleChange={(phonenumber) =>
                            setFieldValue('mobilePhoneNumber', phonenumber)
                        }
                        value={values.mobilePhoneNumber}
                        countrySelected={countrySelected}
                        touched={touched.mobilePhoneNumber}
                        errors={t(errors.mobilePhoneNumber ?? '') ?? ''}
                        fieldName="mobilePhoneNumber"
                        disabled={values.readOnlyField?.includes(
                            'mobilePhoneNumber'
                        )}
                    />
                </Grid>
            </div>

            <div className="onboarding-step4 signup-forms">
                <Grid className="inp-space input-field" item xs={12}>
                    <TextField
                        className={`mais ${conditionalClasses(
                            !!touched.emailAddress && !!errors.emailAddress,
                            'errorBorder'
                        )}`}
                        name="emailAddress"
                        variant="standard"
                        placeholder={
                            t(
                                'onboarding_email_page.onboarding_email_placeholder'
                            ) ?? ''
                        }
                        value={values.emailAddress}
                        error={touched.emailAddress && !!errors.emailAddress}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled={values.readOnlyField?.includes(
                            'emailAddress'
                        )}
                    />
                    {touched.emailAddress && !!errors.emailAddress && (
                        <div className="error-txt">
                            {t(errors.emailAddress)}
                        </div>
                    )}
                </Grid>
            </div>
            <Grid className="privacy-check">
                <Checkbox
                    name="acceptTerms"
                    onChange={handleChange}
                    color="default"
                    checked={values.acceptTerms}
                />
                <span className="p">
                    {t('onboarding_phone_page.onboarding_agreement')}{' '}
                    <a
                        href={GENERATE_POLICY_LINK(
                            language as string,
                            ROUTES.PRIVACY
                        )}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="privacy-policy-btn"
                    >
                        {t('onboarding_phone_page.onboarding_phone_policy')}
                    </a>{' '}
                    {t('onboarding_phone_page.onboarding_phone_and')}{' '}
                    {isUSandCA(values.countrySelected) ? (
                        <a
                            href={`${ROUTES.TERMS_AND_CONDITIONS}en`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="privacy-policy-btn"
                        >
                            {t('onboarding_phone_page.onboarding_phone_tnc')}
                        </a>
                    ) : (
                        <a
                            href={GENERATE_POLICY_LINK(
                                language as string,
                                ROUTES.TERMS_OF_USE
                            )}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="privacy-policy-btn"
                        >
                            {t('onboarding_phone_page.onbaoarding_phone_tou')}
                        </a>
                    )}
                </span>
                {touched.acceptTerms && !!errors?.acceptTerms && (
                    <div className="error-txt">{t(errors.acceptTerms)}</div>
                )}
            </Grid>
            {isUSandCA(values.countrySelected) &&
                (values.userRole === UserRole.Provider ||
                    values.userRole === UserRole.Esthetician) && (
                    <Grid className="privacy-check">
                        <Checkbox
                            name="optInDataSharingAgreement"
                            onChange={handleChange}
                            color="default"
                            checked={values.optInDataSharingAgreement}
                        />
                        <span className="p">
                            {t('onboarding_phone_page.onboarding_agreement')}{' '}
                            <a
                                href={`${ROUTES.DATA_SHARING_AGREEMENT}en`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="privacy-policy-btn"
                            >
                                {t(
                                    'onboarding_phone_page.onboarding_datasharing_agreement'
                                )}
                            </a>
                        </span>
                        {touched.optInDataSharingAgreement &&
                            !!errors?.optInDataSharingAgreement && (
                                <div className="error-txt">
                                    {t(errors.optInDataSharingAgreement)}
                                </div>
                            )}
                    </Grid>
                )}
            <Grid className="privacy-check">
                <Checkbox
                    name="emailConfirmation"
                    onChange={handleChange}
                    color="default"
                    checked={values.emailConfirmation}
                />
                <span className="p">
                    {t('onboarding_email_page.onboarding_email_contact_opt_in')}{' '}
                </span>

                {touched.emailConfirmation && !!errors?.emailConfirmation && (
                    <div className="error-txt">
                        {t(errors.emailConfirmation)}
                    </div>
                )}
            </Grid>
        </div>
    )
}

export default OnboardingStepOne
